<template>
  <div
    tabindex="0"
    class="tags-popover-wrap"
    @mousemove="mouseMoveHandle"
    @mousedown="focusElementHandler"
    @mouseup="blurElementHandler"
  >
    <div
      class="tags-search usn"
      @click.stop="focusInput"
    >
      <input
        ref="searchInput"
        v-model="searchValue"
        v-autofocus
        class="tags-search-input"
        type="text"
        :placeholder="translate('findTagsOrCreateNew')"
        @keyup.enter.prevent.stop="handleEnter"
        @keyup="checkKey"
        @compositionupdate="compositionUpdate"
        @keydown="checkDownKey"
        @input="handleInput"
      />
      <ShortcutKeyPill
        v-if="!quickCard && !isTemplate"
        class="absolute top-3 right-4"
        :text="'T'"
        :background-color="key.background"
        :font-color="key.text"
        :border-color="key.border"
      />
    </div>
    <div
      v-if="searchResultsNotEmpty"
      class="tags-view-popover-content"
      :class="{ scrolling: scrolling }"
    >
      <vue-scroll
        :ref="'searchResultsScroll'"
        :ops="{
          scrollPanel: { scrollingX: false },
          rail: { size: '2px', gutterOfSide: '4px', gutterOfEnds: '0px' },
          bar: { size: '4px', keepShow: true },
        }"
        :style="isMobile ? { 'max-height': dropdownScrollMaxHeight } : ''"
        class="tags-list"
        @handle-scroll="handleScroll"
      >
        <FilterDropdownItem
          v-for="(tag, index) in searchResults"
          :key="tag.id"
          :index="index"
          :item="tag"
          :items="searchResults"
          :selected-item-index="selectedIndex"
          :disable-editing="quickCard || isTemplate"
          @item-hover="itemHover(index)"
          @scroll-to-item="scrollToItem"
          @item-click="handleTagCheckClick(tag.id)"
          @item-right-side-click="singleSelectTag(tag.id)"
          @edit-item="editTag"
        />
      </vue-scroll>
    </div>
    <div
      v-if="showAddTagSection"
      class="buttons-colors-wrap pt-3"
    >
      <div class="select-tag-color-wrap usn">
        <span class="tag-color-label">{{ translate('newTag') }}</span>
        <color-picker
          :selected-color="selectedColor"
          @selectColor="selectColor"
        />
      </div>
      <div
        class="add-new-tag-button pt"
        @click="createNewTag"
      >
        {{ translate('create') }}
        <em
          :style="[getCustomIconStyle(iconPath('tag')), { background: selectedColor }]"
          class="custom-icons mx-1 min-w-4 min-h-4"
        />
        <span class="truncate">{{ searchValue }}</span>
      </div>
    </div>
    <div
      v-if="!searchResultsNotEmpty && !currentProjectId"
      class="tags-notfound-wrap"
      @click.stop
    >
      <span>{{ translate('noTagsFoundSelectBoardToSeeTags') }}</span>
      <span
        class="create-new-workspace-tag"
        @click="goToWorkspaceTagsSettings"
      >
        {{ translate('createNewWorkspaceTagInSettings') }}</span
      >
    </div>
  </div>
</template>

<script>
import { nanoid } from 'nanoid';
import cardTagsMixin from '@/mixins/cardTagsMixin';
import inputDropdown from '@/mixins/inputDropdown';
import deleteTagFromCardMixin from '@/mixins/deleteTagFromCardMixin';
import customIconsMixin from '@/mixins/customIconsMixin';
import FilterDropdownItem from '@/components/widgets/filters/FilterDropdownItem';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import defaultShortcutKeyPillStyle from '@/mixins/defaultShortcutKeyPillStyle';
import ShortcutKeyPill from '@/components/functional/ShortcutKeyPill';
import scrollToElementInVuescroll from '@/utilities/scrollToElementInVuescroll';
import { groupByOption } from '@/types/groupByOption';
import ColorPicker from '@/components/widgets/ColorPicker';
import { ResourceType } from '@/types/resources';
import { getSprintKey } from '@/utilities/sprintKeys';

export default {
  name: 'CardTagsViewPopup',
  components: {
    FilterDropdownItem,
    ShortcutKeyPill,
    ColorPicker,
  },
  mixins: [
    cardTagsMixin,
    deleteTagFromCardMixin,
    customIconsMixin,
    inputDropdown,
    elementFocusMixin,
    defaultShortcutKeyPillStyle,
  ],

  inject: {
    currentCardId: {
      type: String,
      required: false,
      default: '',
    },
    emitMessages: {
      type: Boolean,
      required: false,
      default: false,
    },
    popoverPlacement: {
      type: String,
      required: false,
      default: 'left-start',
    },
    quickCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableCreateWorkspaceTag: {
      type: Boolean,
      required: false,
      default: true,
    },
    isQuickCardModal: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  props: {
    cardId: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['esc', 'showEditTagPopup', 'tagSelected', 'singleTagSelected', 'closePopup'],

  data() {
    return {
      searchValue: '',
      displayAddButton: false,
      selectedColor: '',
      sortedTags: [],
      selectedIndex: 0,
      scrollTimeout: null,
      scrolling: false,
      dropdownScrollMaxHeight: '',
    };
  },
  computed: {
    currentQuickCard() {
      return this.isQuickCardModal
        ? this.$store.getters.getQuickCard
        : this.$store.getters.getQuickChildCard;
    },

    currentCardIdForPopup() {
      return this.currentCardId || this.$store.getters.currentCardIdForPopup || this.cardId;
    },

    filteredTags() {
      const queries = this.searchValue.toLowerCase().split(' ');
      if (!this.searchValue) return this.sortedTags;

      return this.sortedTags.filter(({ name }) =>
        queries.some((q) => {
          if (!q) return false;
          return name.toLowerCase().includes(q);
        })
      );
    },

    searchResults() {
      if (this.tagsLength === 0) return [];
      return this.filteredTags.map((t) => {
        const { id, name, color, project_id } = t;
        return {
          id,
          label: name,
          color,
          icon: 'tag',
          key: 'tags',
          customIcon: true,
          customIconColor: color,
          checkbox: true,
          checked: this.isAddedToCurrentCard(t.id),
          lighter: true,
          avatar: false,
          editable: project_id,
          isTag: true,
        };
      });
    },

    currentCard() {
      if ((this.$store.getters.getQuickCardIsOn || this.childCardCreation) && !this.isTemplate) {
        return this.currentQuickCard;
      }
      if (
        [
          this.$constants.settingsTemplate.card.create.name,
          this.$constants.settingsTemplate.card.edit.name,
        ].includes(this.$route.name)
      ) {
        return this.$store.getters.getCardTemplateDraft;
      }

      return this.$store.getters.getCardById(this.currentCardId || this.cardId);
    },

    isEpic() {
      return this.currentCard.type === ResourceType.Epic;
    },

    noTagsFoundText() {
      return this.isEpic
        ? this.translate('epicNoTagsFound')
        : this.translate('noTagsFoundSelectBoardToSeeTags');
    },

    cardTags() {
      if (this.isTemplate) {
        return (this.currentCard.card_tags?.[0]?.ids || [])
          .map((id) => this.tags[id])
          .filter(Boolean);
      }
      return this.currentCard.tags || [];
    },

    searchResultsNotEmpty() {
      return this.filteredTags && this.filteredTags.length > 0;
    },

    existingTag() {
      return this.filteredTags.some(
        ({ name }) => name.toLowerCase() === this.searchValue.toLowerCase()
      );
    },

    showAddTagSection() {
      return (this.displayAddButton || !this.tagsLength) && !this.isCreateWorkspaceTagDisabled;
    },

    buttonDisabled() {
      return !this.searchValue || !this.selectedColor;
    },

    childCardCreation() {
      return this.$store.getters.getChildCardCreation;
    },

    isChildCardOrTemplate() {
      return this.quickCard || this.childCardCreation || this.isTemplate;
    },

    templatePopoverClass() {
      return this.isTemplate ? 'template-tags-dropdown' : '';
    },

    popoverClass() {
      const additionalClass =
        this.quickCard || this.childCardCreation
          ? 'quick-card-tags-dropdown'
          : this.templatePopoverClass;
      return `card-tags-view-popover-wrap card-sidebar-popover ${additionalClass}`;
    },

    isCreateWorkspaceTagDisabled() {
      return this.disableCreateWorkspaceTag && !this.currentProjectId;
    },

    getCurrentQuickCardProjectId() {
      return this.isQuickCardModal
        ? this.$store.getters.getQuickCardProjectId
        : this.$store.getters.getQuickChildCardProjectId;
    },
  },
  watch: {
    tagsLength(newVal) {
      if (newVal) {
        this.sortTagsInCurrentBoard();
      }
    },
    searchValue() {
      if (this.isMobile) this.handleInput();
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (!this.isMobile) {
        this.focusInput();
      }
    });

    this.sortTagsInCurrentBoard();
  },

  unmounted() {
    clearTimeout(this.scrollTimeout);
  },
  methods: {
    handleEsc(e) {
      if (this.isChildCardOrTemplate) {
        this.$emit('esc');
        return;
      }
      e.stopPropagation();
      this.closeTagsPopover();
    },
    handleScroll() {
      if (this.scrolling) return;
      this.scrolling = true;
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.scrolling = false;
      }, 400);
    },

    focusInput() {
      this.$refs.searchInput?.focus();
    },

    itemHover(index) {
      this.setSelectedIndex(index);
    },

    scrollToItem(event) {
      scrollToElementInVuescroll(event, this.$refs.searchResultsScroll);
    },

    selectSearchResult(item) {
      this.singleSelectTag(item.id);
    },

    selectOnlySearchResult(item) {
      this.handleTagCheckClick(item.id);
    },

    closeTagsPopover(willOpenEditTagPopup = false) {
      if (this.isFocusElement()) return;

      if (willOpenEditTagPopup) {
        this.$emit('showEditTagPopup');
      } else {
        this.$emit('closePopup');
      }
    },

    sortTagsInCurrentBoard() {
      this.sortedTags = [...this.projectWorkspaceTags]
        .map((id) => this.tags[id])
        .sort((a, b) => {
          const cardHasTagA = this.cardTags.some(({ id }) => a.id === id);
          const cardHasTagB = this.cardTags.some(({ id }) => b.id === id);

          if (cardHasTagA && !cardHasTagB) return -1;
          if (!cardHasTagA && cardHasTagB) return 1;
          return a.name?.localeCompare(b?.name);
        });
    },

    createNewTag() {
      if (this.isCreateWorkspaceTagDisabled) return;
      const currentCard = this.currentCard;

      if (!this.searchValue || this.existingTag || !this.selectedColor) return;
      const newTag = {
        name: this.searchValue,
        color: this.selectedColor,
      };

      const localTagId = nanoid(6);
      const newLocalTag = {
        ...newTag,
        id: localTagId,
        project_id: this.currentProjectId,
        team_id: this.currentTeamId,
      };
      this.$store.dispatch('addLocalTag', { tag: newLocalTag });

      const currentTags = [...this.cardTags];
      const newTags = [...currentTags];

      newTags.push(newLocalTag);

      if (!this.isChildCardOrTemplate) {
        this.$store.dispatch('localUpdateCardField', {
          cardId: currentCard.id,
          fieldName: 'tags',
          fieldValue: newTags,
        });
      }

      this.$store
        .dispatch('createTag', { tag: newTag, projectId: this.currentProjectId })
        .then(({ data }) => {
          this.$store.dispatch('addLocalTag', { tag: data.tag });
          this.$store.dispatch('deleteLocalTag', { tagId: localTagId });
          if (this.quickCard || this.childCardCreation) {
            this.$store.dispatch('addTagToQuickCard', {
              tag: { ...data.tag, checked: true },
              isModal: this.isQuickCardModal,
            });
            return;
          }
          if (this.isTemplate) {
            this.$store.dispatch('addTagToTemplate', data.tag.id);
            return;
          }
          this.$store.dispatch('addTagToCard', { cardId: currentCard.id, tagId: data.tag.id });
          this.updateGroupByOrder(currentCard, data.tag);
          this.$store.dispatch('updateLocalCardTag', {
            cardId: currentCard.id,
            tagId: localTagId,
            tag: data.tag,
          });
        })
        .catch(() => {
          // delete local tag
          this.$store.dispatch('deleteLocalTag', {
            tagId: localTagId,
          });
          if (this.isChildCardOrTemplate) return;
          this.$store.dispatch('localUpdateCardField', {
            cardId: currentCard.id,
            fieldName: 'tags',
            fieldValue: currentTags,
          });
        });
      this.closeTagsPopover();
    },

    updateGroupByOrder(card, tag) {
      const boardId = card.sprint_id
        ? getSprintKey(card.project_id, card.sprint_id)
        : card.board_id;
      this.$store.dispatch('addGroupByList', {
        listId: tag.id,
        groupBy: groupByOption.tag,
      });
      this.$store.dispatch('addCardToGroupByList', {
        boardId,
        cardId: card.id,
        listId: tag.id,
        groupBy: groupByOption.tag,
      });
      this.$store.dispatch('removeCardFromGroupByList', {
        boardId,
        cardId: card.id,
        listId: `${boardId}-notags`,
        groupBy: groupByOption.tag,
      });
    },

    handleEnter() {
      if (this.searchResults.length) return;

      this.createNewTag();
    },

    editTag(tagItem) {
      const tag = this.tags[tagItem.id];
      this.$store.dispatch('setCurrentTagForEdit', tag);

      this.closeTagsPopover(true);
    },

    isAddedToCurrentCard(tagId) {
      return this.cardTags.some((t) => t.id === tagId);
    },

    handleTagCheckClick(tagId) {
      const tag = this.tags[tagId];
      const cardId = this.currentCardIdForPopup;

      this.focusInput();
      if (this.emitMessages) {
        this.$emit('tagSelected', tag);
        return;
      }

      if (this.isAddedToCurrentCard(tagId)) {
        this.deleteTagFromCard(tag, cardId);
      } else {
        this.$store.dispatch('localAddTagToCard', { cardId, tag });
        this.$store.dispatch('addTagToCard', { cardId, tagId }).catch(() => {
          this.$store.dispatch('localDeleteTagFromCard', { cardId, tagId });
        });
      }
    },

    handleInput() {
      if (this.isCreateWorkspaceTagDisabled) return;

      if (!this.searchValue || this.existingTag) {
        this.turnOffDisplayAddButton();
        return;
      }
      if (this.searchValue && !this.searchResultsNotEmpty) {
        this.triggerDisplayAddButton();
        return;
      }

      this.triggerDisplayAddButton();
    },

    turnOffDisplayAddButton() {
      this.displayAddButton = false;
      this.selectedColor = '';
    },

    triggerDisplayAddButton() {
      this.displayAddButton = true;
      this.selectRandomColor();
    },

    singleSelectTag(tagId) {
      if (this.emitMessages) {
        this.$emit('singleTagSelected', this.tags[tagId]);
        return;
      }
      this.handleTagCheckClick(tagId);
      this.closeTagsPopover();
    },

    goToWorkspaceTagsSettings() {
      this.$router.push({ name: 'tags' });
    },
  },
};
</script>

<style lang="scss">
$inputTagRightLeftPadding: 16px;

.tag-settings-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  height: 56px;
  box-sizing: border-box;
  border-top: 1px solid #ededed;

  span {
    font-size: 14px;
    line-height: 19px;
    letter-spacing: 0.3px;
    color: $stLightBlack;
  }
}

.tags-search {
  cursor: text;

  .tags-search-input {
    border: none;
    outline: none;
    font-size: 14px;
    color: $stPrimaryColor;
    line-height: 20px;
    padding: 12px $inputTagRightLeftPadding;
    width: calc(100% - $inputTagRightLeftPadding);
    font-weight: 500;
    @apply text-text-default bg-important-bg;

    &::placeholder {
      @apply text-text-placeholder;
    }
  }
}

.tags-view-popover-content {
  border-top: 1px solid;
  @apply border-dividers-default;

  &.scrolling {
    .filter-dropdown-item {
      pointer-events: none;
    }
  }

  .tag-color-label {
    padding: 0 8px;
  }

  .tags-list {
    max-height: 328px;
    height: auto !important;
    display: flex;
    flex-direction: column;

    .__panel {
      padding: 0px 0px 4px;
    }

    .tag-list-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 10px 16px;
      max-height: 40px;
      box-sizing: border-box;

      .v-popover {
        &.card-tag-wrap {
          line-height: 0;
        }
      }

      .tag-edit-icon {
        height: 16px;
        width: 16px;
        min-width: 16px;
        max-width: 16px;
        background-color: $gray600;
        display: none;
        overflow: hidden;
        margin-left: 8px;
      }

      .tags-actions-wrap {
        display: flex;
        align-items: center;
        margin-right: 14px;

        .hide-tag-check {
          opacity: 0;
        }

        .tag-icon {
          font-size: 12px;
          min-width: 12px;
          min-height: 12px;
          max-width: 12px;
          max-height: 12px;
          padding: 1px;
          overflow: hidden;
          color: $gray300;
          transition: 0.3s ease-in-out;
          border: 1px solid $gray300;
          transition-property: color, border;
          border-radius: 4px;

          &.checked {
            color: $stBlue;
            border: 1px solid $stBlue;
            background-color: $primary50;
          }
        }
      }

      &:hover {
        background-color: $gray100;

        .tag-edit-icon {
          display: inline;
        }
      }
    }
  }
}

.tags-notfound-wrap {
  border-top: 1px solid;
  @apply p-[15px]
    text-sm
    border-dividers-default;

  span {
    @apply text-text-default;

    &.create-new-workspace-tag {
      @apply cursor-pointer underline;
    }
  }
}
</style>
